import { Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {EndpointsService} from "../endpoints.service";
import {cloneDeep} from "lodash";
import {DeviceModel, InterferenceTestDeviceModel} from "../../models/grit/grit.device-model";
import {ReceiverModel} from "../../models/grit/grit.receiver-model";
import {InterferenceTestEntities, InterferenceTestModel} from "../../models/grit/grit.interference-test-model";
import {RadarModel} from "../../models/grit/grit.radar-model";
import {sectorizationGraphDataGrit} from "../../models/sectorizationGraphData";
import {UserProfile} from "./grit.user.service";


@Injectable({
  providedIn: 'root'
})
export class GritApiService {
  constructor(
    private http: HttpClient,
    private endpointService: EndpointsService,
  ){}

  //#region Others
  calculateImpactAssessment(impactAssessmentDTO : any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.http.post(
        this.endpointService.gritCalculateImpactAssessment,
        impactAssessmentDTO,
        this.endpointService.httpOptions
      ).subscribe({
        error: (response) => reject(response)
      })
    });
  }
  sectorizationDisplay(sectData : sectorizationGraphDataGrit): Promise<sectorizationGraphDataGrit> {
    return new Promise<sectorizationGraphDataGrit>((resolve, reject) => {
      this.http.post(
        this.endpointService.gritSectorization,
        sectData,
        this.endpointService.httpOptions
      ).subscribe({
        next: (data: sectorizationGraphDataGrit) => resolve(data),
        error: (response) => reject(response)
      })
    });
  }
  //endregion


  //#region User Profile
  userProfileCurrent(): Promise<UserProfile>{
    return new Promise<UserProfile>((resolve, reject)  => {
      this.http.get<UserProfile>(
        this.endpointService.gritUserProfile,
        this.endpointService.httpOptions
      ).subscribe({
        next: (userProfileDTO) => resolve(new UserProfile().fromDTO(userProfileDTO)),
        error: (error) => reject(null)
      })
    });
  }

  userProfileUpdateFirsSubscribed(firsSubscribed : string): Promise<UserProfile>{
    return new Promise<UserProfile>((resolve, reject)  => {
      this.http.put(
        `${this.endpointService.gritUserProfile}/firs-subscribed`,
        firsSubscribed,
        this.endpointService.httpOptions
      ).subscribe({
        next: (userProfileDTO) => resolve(new UserProfile().fromDTO(userProfileDTO)),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  userProfileUpdateMailingList(mailingList : string): Promise<UserProfile>{
    return new Promise<UserProfile>((resolve, reject)  => {
      this.http.put(
        `${this.endpointService.gritUserProfile}/mailing-list`,
        mailingList,
        this.endpointService.httpOptions
      ).subscribe({
        next: (userProfileDTO) => resolve(new UserProfile().fromDTO(userProfileDTO)),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //#endregion


  //#region Device
  deviceGetAll(): Promise<DeviceModel[]>{
    return new Promise<DeviceModel[]>((resolve, reject)  => {
      this.http.get<DeviceModel[]>(
        this.endpointService.gritDevice,
        this.endpointService.httpOptions
      ).subscribe({
        next: (devicesDTO) => resolve(devicesDTO.map(device => new DeviceModel().fromDTO(device))),
        error: (error) => reject(null)
      })
    });
  }

  deviceCreate(devices : DeviceModel[]): Promise<DeviceModel[]>{
    return new Promise<DeviceModel[]>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritDevice,
        devices.map(d => d.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(d => new DeviceModel().fromDTO(d))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  deviceUpdate(devices : DeviceModel[]): Promise<DeviceModel[]>{
    return new Promise<DeviceModel[]>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritDevice,
        devices.map(device => device.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(device => new DeviceModel().fromDTO(device))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  deviceDelete(devices : DeviceModel[]): Promise<DeviceModel[]>{
    let options = cloneDeep(this.endpointService.httpOptions)
    options.body = devices.map(device => device.toDTO());

    return new Promise<DeviceModel[]>((resolve, reject)  => {
      this.http.delete(
        this.endpointService.gritDevice,
        options
      ).subscribe({
        next: (response) => resolve(response["result"].map(device => new DeviceModel().fromDTO(device))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //endregion


  //#region Interference Test Device
  itDeviceGetAll(): Promise<InterferenceTestDeviceModel[]>{
    return new Promise<InterferenceTestDeviceModel[]>((resolve, reject)  => {
      this.http.get<InterferenceTestDeviceModel[]>(
        this.endpointService.gritInterferenceTestDevice,
        this.endpointService.httpOptions
      ).subscribe({
        next: (itDevicesDTO) => resolve(itDevicesDTO.map(itDevice => new InterferenceTestDeviceModel().fromDTO(itDevice))),
        error: (error) => reject(null)
      })
    });
  }

  itDeviceCreate(itDevice : InterferenceTestDeviceModel[]): Promise<InterferenceTestDeviceModel[]>{
    return new Promise<InterferenceTestDeviceModel[]>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritInterferenceTestDevice,
        itDevice.map(d => d.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(d => new InterferenceTestDeviceModel().fromDTO(d))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  itDeviceUpdate(itDevices : InterferenceTestDeviceModel[]): Promise<InterferenceTestDeviceModel[]>{
    return new Promise<InterferenceTestDeviceModel[]>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritInterferenceTestDevice,
        itDevices.map(itDevice => itDevice.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(itDevice => new InterferenceTestDeviceModel().fromDTO(itDevice))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  itDeviceDelete(itDevices : InterferenceTestDeviceModel[]): Promise<InterferenceTestDeviceModel[]>{
    let options = cloneDeep(this.endpointService.httpOptions)
    options.body = itDevices.map(itDevice => itDevice.toDTO());

    return new Promise<InterferenceTestDeviceModel[]>((resolve, reject)  => {
      this.http.delete(
        this.endpointService.gritInterferenceTestDevice,
        options
      ).subscribe({
        next: (response) => resolve(response["result"].map(itDevice => new InterferenceTestDeviceModel().fromDTO(itDevice))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //endregion


  //#region Receiver
  receiverGetAll(): Promise<ReceiverModel[]>{
    return new Promise<ReceiverModel[]>((resolve, reject)  => {
      this.http.get<ReceiverModel[]>(
        this.endpointService.gritReceiver,
        this.endpointService.httpOptions
      ).subscribe({
        next: (receiversDTO) => resolve(receiversDTO.map(receiver => new ReceiverModel().fromDTO(receiver))),
        error: (error) => reject(null)
      })
    });
  }

  receiverCreate(receivers : ReceiverModel[]): Promise<ReceiverModel[]>{
    return new Promise<ReceiverModel[]>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritReceiver,
        receivers.map(r => r.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(r => new ReceiverModel().fromDTO(r))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  receiverUpdate(receivers : ReceiverModel[]): Promise<ReceiverModel[]>{
    return new Promise<ReceiverModel[]>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritReceiver,
        receivers.map(receiver => receiver.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(receiver => new ReceiverModel().fromDTO(receiver))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  receiverDelete(receivers : ReceiverModel[]): Promise<ReceiverModel[]>{
    let options = cloneDeep(this.endpointService.httpOptions)
    options.body = receivers.map(receiver => receiver.toDTO());

    return new Promise<ReceiverModel[]>((resolve, reject)  => {
      this.http.delete(
        this.endpointService.gritReceiver,
        options
      ).subscribe({
        next: (response) => resolve(response["result"].map(receiver => new ReceiverModel().fromDTO(receiver))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //endregion


  //#region Interference Test
  interferenceTestCreate(projectId : number, originator: string): Promise<InterferenceTestModel>{
    return new Promise<InterferenceTestModel>((resolve, reject)  => {
      this.http.post(
        `${this.endpointService.gritInterferenceTest}?projectId=${projectId}&originator=${originator}`,
        null,
        this.endpointService.httpOptions
      ).subscribe({
        // next: (response) => resolve(new InterferenceTestModel().fromDTO(response["result"])),
        next: (response) => resolve(null), //TODO
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  interferenceTestDelete(projectId : number): Promise<InterferenceTestModel>{
    return new Promise<InterferenceTestModel>((resolve, reject)  => {
      this.http.delete(
        `${this.endpointService.gritInterferenceTest}?projectId=${projectId}`,
        this.endpointService.httpOptions
      ).subscribe({
        // next: (response) => resolve(new InterferenceTestModel().fromDTO(response["result"])),
        next: (response) => resolve(null), //TODO
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  interferenceTestGetAll(): Promise<InterferenceTestModel[]>{
    return new Promise<InterferenceTestModel[]>((resolve, reject)  => {
      this.http.get<InterferenceTestModel[]>(
        this.endpointService.gritInterferenceTest + "/all",
        this.endpointService.httpOptions
      ).subscribe({
        next: (interferenceTestsDTO) => resolve(interferenceTestsDTO.map(
          interferenceTest => new InterferenceTestModel().fromDTO(interferenceTest))),
        error: (error) => reject(null)
      })
    });
  }

  interferenceTestGet(): Promise<InterferenceTestModel>{
    return new Promise<InterferenceTestModel>((resolve, reject)  => {
      this.http.get<InterferenceTestModel>(
        this.endpointService.gritInterferenceTest ,
        this.endpointService.httpOptions
      ).subscribe({
        next: (interferenceTestDTO) => resolve(new InterferenceTestModel().fromDTO(interferenceTestDTO)),
        error: (error) => resolve(null)
      })
    });
  }

  interferenceTestGetEntities(id: number): Promise<InterferenceTestEntities>{
    return new Promise<InterferenceTestEntities>((resolve, reject)  => {
      this.http.get<InterferenceTestEntities>(
        this.endpointService.gritInterferenceTest + `/${id}`,
        this.endpointService.httpOptions
      ).subscribe({
        next: (interferenceTestEntities) => resolve(new InterferenceTestEntities().fromDTO(interferenceTestEntities)),
        error: (error) => reject(null)
      })
    });
  }

  interferenceTestUpdate(interferenceTest : InterferenceTestModel): Promise<InterferenceTestModel>{
    return new Promise<InterferenceTestModel>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritInterferenceTest,
        interferenceTest.toDTO(),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(new InterferenceTestModel().fromDTO(response["result"])),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  interferenceTestUpdateNOTAM(id : number, type: string, text: string): Promise<string>{
    return new Promise<string>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritInterferenceTest + `/${id}/NOTAM`,
        {type: type, text: text},
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"] as string),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  interferenceTestSendNOTAM(id: number, emails : string[], text: string): Promise<boolean>{
    return new Promise<boolean>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritInterferenceTest + `/${id}/NOTAM`,
        {emails: emails, text: text},
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(true),
        error: (response) => reject(false)
      })
    });
  }

  interferenceTestTrigger(trigger : string): Promise<InterferenceTestModel>{
    return new Promise<InterferenceTestModel>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritInterferenceTest + `/trigger/${trigger}`,
        null,
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(new InterferenceTestModel().fromDTO(response["result"])),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //endregion


  //#region Radar
  radarGetAll(): Promise<RadarModel[]>{
    return new Promise<RadarModel[]>((resolve, reject)  => {
      this.http.get<RadarModel[]>(
        this.endpointService.gritRadar,
        this.endpointService.httpOptions
      ).subscribe({
        next: (radarsDTO) => resolve(radarsDTO.map(radar => new RadarModel().fromDTO(radar))),
        error: (error) => reject(null)
      })
    });
  }

  radarCreate(radar : RadarModel): Promise<RadarModel[]>{
    return new Promise<RadarModel[]>((resolve, reject)  => {
      this.http.post(
        this.endpointService.gritRadar,
        radar.toDTO(),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve([new RadarModel().fromDTO(response["result"])]),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  radarUpdate(radars : RadarModel[]): Promise<RadarModel[]>{
    return new Promise<RadarModel[]>((resolve, reject)  => {
      this.http.put(
        this.endpointService.gritRadar,
        radars.map(radar => radar.toDTO()),
        this.endpointService.httpOptions
      ).subscribe({
        next: (response) => resolve(response["result"].map(radar => new RadarModel().fromDTO(radar))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }

  radarDelete(radars : RadarModel[]): Promise<RadarModel[]>{
    let options = cloneDeep(this.endpointService.httpOptions) as any
    options.body = radars.map(radar => radar.toDTO());

    return new Promise<RadarModel[]>((resolve, reject)  => {
      this.http.delete(
        this.endpointService.gritRadar,
        options
      ).subscribe({
        next: (response) => resolve(response["result"].map(radar => new RadarModel().fromDTO(radar))),
        error: (response) => reject(response["error"]["errors"])
      })
    });
  }
  //endregion
}
